import type { HTMLAttributes, JSX } from 'react';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
}

const getVariantClasses = (variant: BadgeVariant): string => {
  switch (variant) {
    case 'default':
      return 'bg-gray-100 text-gray-800';
    case 'primary':
      return 'bg-info-light text-info-dark';
    case 'success':
      return 'bg-success-light text-success';
    case 'warning':
      return 'bg-warning-light text-warning';
    case 'error':
      return 'bg-error-light text-error';
  }
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-sm',
  lg: 'px-3 py-1 text-base',
};

export function Badge({
  variant = 'default',
  size = 'md',
  className = '',
  children,
  ...props
}: BadgeProps): JSX.Element {
  const baseStyles = 'inline-flex items-center font-medium rounded-full';

  const combinedClassName = [
    baseStyles,
    sizeStyles[size],
    getVariantClasses(variant),
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={combinedClassName} {...props}>
      {children}
    </span>
  );
}
